import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, chileInputToUTC } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import RichTextEditor from '../components/RichTextEditor';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, Ticket } from 'lucide-react';
import { toast } from 'sonner';

export default function NewTicketPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'media',
    category_id: '',
    gerencia_area_id: '',
    due_date: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchGerencias();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchGerencias = async () => {
    try {
      const response = await axios.get(`${API_URL}/gerencias`);
      setGerencias(response.data);
    } catch (error) {
      console.error('Error fetching gerencias:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gerencia_area_id) {
      toast.error('Selecciona una gerencia/área');
      return;
    }
    setLoading(true);
    
    try {
      const payload = {
        ...form,
        due_date: chileInputToUTC(form.due_date),
      };
      const response = await axios.post(`${API_URL}/tickets`, payload);
      toast.success('Ticket creado exitosamente');
      navigate(`/tickets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="new-ticket-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-gray-600" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-800">Nuevo Ticket</h1>
          <p className="text-gray-500">Describe tu solicitud o problema</p>
        </div>
      </div>

      {/* Form */}
      <Card className="border-gray-200 bg-white shadow-sm" data-testid="ticket-form-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-gray-800">
            <Ticket className="w-5 h-5 text-[#7FD02E]" />
            Información del Ticket
          </CardTitle>
          <CardDescription>
            Complete los campos para crear un nuevo ticket de soporte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-gray-700">Título *</Label>
              <Input
                id="title"
                placeholder="Describe brevemente el problema o solicitud"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                maxLength={255}
                className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                data-testid="ticket-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-gray-700">Descripción</Label>
              <RichTextEditor
                value={form.description}
                onChange={(html) => setForm({ ...form, description: html })}
                placeholder="Proporciona más detalles sobre tu solicitud. Puedes dar formato con negrita, listas, enlaces, etc."
                minHeight={160}
                data-testid="ticket-description-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Gerencia / Área *</Label>
                <Select
                  value={form.gerencia_area_id}
                  onValueChange={(value) => setForm({ ...form, gerencia_area_id: value })}
                >
                  <SelectTrigger data-testid="ticket-gerencia-select">
                    <SelectValue placeholder={gerencias.length === 0 ? 'Sin gerencias configuradas' : 'Seleccionar gerencia/área'} />
                  </SelectTrigger>
                  <SelectContent>
                    {gerencias.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {gerencias.length === 0 && (
                  <p className="text-xs text-amber-600">Pídele a la jefatura que cree gerencias/áreas en el menú "Gerencias".</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Categoría</Label>
                <Select
                  value={form.category_id}
                  onValueChange={(value) => setForm({ ...form, category_id: value })}
                >
                  <SelectTrigger data-testid="ticket-category-select">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Prioridad *</Label>
                <Select
                  value={form.priority}
                  onValueChange={(value) => setForm({ ...form, priority: value })}
                >
                  <SelectTrigger data-testid="ticket-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-400" />
                        Baja
                      </span>
                    </SelectItem>
                    <SelectItem value="media">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-400" />
                        Media
                      </span>
                    </SelectItem>
                    <SelectItem value="alta">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        Alta
                      </span>
                    </SelectItem>
                    <SelectItem value="critica">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-rose-400" />
                        Crítica
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="due_date" className="text-gray-700">Fecha límite</Label>
                <Input
                  id="due_date"
                  type="datetime-local"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                  data-testid="ticket-due-date-input"
                />
                <p className="text-xs text-gray-400">Opcional. Para seguimiento.</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="text-gray-600"
                data-testid="cancel-btn"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#7FD02E] hover:bg-[#6BC024] text-white" 
                disabled={loading || !form.title || !form.gerencia_area_id}
                data-testid="submit-ticket-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Crear Ticket
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
